import { useState, useCallback } from 'react';

interface UseFloatingConverterReturn {
    isOpen: boolean;
    initialValue: string;
    initialMode: 'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token';
    openConverter: (value?: string, mode?: 'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token') => void;
    closeConverter: () => void;
}

/**
 * Hook to manage the floating converter state globally
 */
export const useFloatingConverter = (): UseFloatingConverterReturn => {
    const [isOpen, setIsOpen] = useState(false);
    const [initialValue, setInitialValue] = useState('');
    const [initialMode, setInitialMode] = useState<'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token'>('auto');

    const openConverter = useCallback((value = '', mode: 'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token' = 'auto') => {
        setInitialValue(value);
        setInitialMode(mode);
        setIsOpen(true);
    }, []);

    const closeConverter = useCallback(() => {
        setIsOpen(false);
        // Clear values after a short delay to allow for smooth closing animation
        setTimeout(() => {
            setInitialValue('');
            setInitialMode('auto');
        }, 300);
    }, []);

    return {
        isOpen,
        initialValue,
        initialMode,
        openConverter,
        closeConverter
    };
};

// Global keyboard shortcut handler
export const useConverterKeyboardShortcut = (openConverter: (value?: string) => void) => {
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        // Ctrl+H or Cmd+H to open converter
        if ((event.ctrlKey || event.metaKey) && event.key === 'h') {
            event.preventDefault();

            // Try to get selected text
            const selection = window.getSelection();
            const selectedText = selection?.toString().trim() || '';

            openConverter(selectedText);
        }
    }, [openConverter]);

    return handleKeyDown;
};
